/* Factories */
import { createBarchart } from "src/utils/layout/stacked-layout/factories/createBarchart";
import { createHistogram } from "src/utils/layout/stacked-layout/factories/createHistogram";
import { createSurvivalPlot } from "src/utils/layout/stacked-layout/factories/createSurvivalPlot";

/* Utils */
import createLabel from "src/utils/layout/utils/createLabel";
import createChartLegend from "src/utils/layout/utils/createLegend";

/* Constants */
import { ChartType } from "src/shared/constants/charts/Chart";
import {
  cohortLookedForGroups,
  sumGroupLookedForNoValue,
  sumGroupTotals,
  sumVisibleGroupTotals,
} from "src/shared/constants/charts/NoValueStates";

/* Interfaces */
import { BaseChartProps } from "src/shared/interfaces/charts/BaseChart";
import { DescriptiveStatisticsProps } from "src/shared/interfaces/DescriptiveStatistics";
import { SurvivalAnalysisProps } from "src/shared/interfaces/SurvivalAnalysis";
import { ComponentProps } from "src/shared/interfaces/common/Layout";

export const createStackedLayout = (
  index: number,
  type: ChartType,
  data: BaseChartProps<
    DescriptiveStatisticsProps | SurvivalAnalysisProps,
    ChartType[]
  >,
  featureOrCohort?: string,
  showNoValueEntries?: boolean
): ComponentProps[] => {
  const typedData = data.data[type];

  // The displayed numerator `n` counts patients where the feature was looked
  // for AND the cohort-defining value was looked for. On the cohort side that
  // means every group except the "Not looked for" cohort group (its members
  // have no cohort value at all).
  const lookedForCohortGroups = cohortLookedForGroups(typedData);

  // Has-value base of `n`, per toggle state:
  //  - OFF: only the real cohort groups are shown, so count just those
  //    "complete case" patients (real feature value AND real cohort value).
  //  - ON: every cohort-looked-for group (real groups + the "Looked for, no
  //    value" cohort group), excluding the "Not looked for" cohort group whose
  //    has-value patients the backend total would otherwise wrongly include.
  //  - undefined (chart types without the toggle, e.g. survival analysis):
  //    keep the original backend total.
  const total =
    showNoValueEntries === false
      ? sumVisibleGroupTotals(typedData)
      : showNoValueEntries === true
        ? sumGroupTotals(lookedForCohortGroups)
        : data.total;

  // Looked-for-no-value add-on of `n` (folded in by ChartLabel when the toggle
  // is on): the feature-side looked-for-no-value counts live per cohort group,
  // so aggregate them across the cohort-looked-for groups. Falls back to the
  // response-level field when no per-group counts exist (non-descriptive
  // charts).
  const lookedForNoValueTotal =
    sumGroupLookedForNoValue(lookedForCohortGroups) ??
    data.lookedForNoValueTotal;

  // Create label and legend components
  const label = createLabel(
    data,
    featureOrCohort,
    total,
    data.totalCohortSize,
    data.notLookedForCount,
    data.lookedForNoValueCount,
    lookedForNoValueTotal
  );
  const legend = createChartLegend(
    type,
    data,
    showNoValueEntries
  );

  const chartRenderer = () => {
    switch (type) {
      case ChartType.BAR:
        return createBarchart(
          index,
          label,
          typedData,
          legend,
          featureOrCohort
        );
      case ChartType.HISTOGRAM:
        return createHistogram(
          index,
          label,
          data,
          legend,
          featureOrCohort
        );
      case ChartType.SCATTER:
        return createSurvivalPlot(
          index,
          label,
          data,
          legend,
          featureOrCohort,
          showNoValueEntries
        );
      default:
        return [];
    }
  };

  return chartRenderer();
};
